import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useI18n } from "./i18n";

const API = process.env.REACT_APP_API_URL || "";

const Wrap = styled.div`
  min-height:100vh; display:flex; align-items:center; justify-content:center;
  background:${({theme})=>theme?.bg||"#080a0c"}; padding:1rem;
`;
const Card = styled.div`
  background:${({theme})=>theme?.bgCard||"#0e1115"};
  border:1px solid ${({theme})=>theme?.border||"#1e2530"};
  border-radius:16px; padding:2.5rem; max-width:440px; width:100%;
  text-align:center;
`;
const Title = styled.h1`
  font-family:${({theme})=>theme?.fontDisplay||"serif"};
  font-size:1.4rem; color:${({theme})=>theme?.text||"#e8edf2"};
  margin-bottom:.75rem;
`;
const Text = styled.p`
  font-size:.85rem; color:${({theme})=>theme?.textDim||"#8a9aaa"};
  line-height:1.6; margin-bottom:1.5rem;
`;
const Btn = styled.button`
  padding:.7rem 1.5rem; border-radius:8px; border:none; cursor:pointer;
  font-weight:700; font-size:.85rem; margin:.3rem;
  background:${({$danger,theme}) => $danger ? (theme?.red||"#ff3b5c") : (theme?.bgHover||"#141820")};
  color:${({$danger}) => $danger ? "#fff" : "#e8edf2"};
  &:hover { opacity:.85; }
  &:disabled { opacity:.5; cursor:not-allowed; }
`;
const Email = styled.strong`color:${({theme})=>theme?.accent||"#4fc3f7"};`;

export default function UnsubscribePage() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(null);   // "marketing" | "all" | "resubscribed"
  const [submitting, setSubmitting] = useState(false);

  // Récupérer le token depuis l'URL
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  const campaignId = params.get("c");

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    fetch(`${API}/api/v1/email/unsubscribe/info/?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(data => setInfo(data))
      .catch(() => setInfo({ valid: false, error: "Erreur réseau." }))
      .finally(() => setLoading(false));
  }, [token]);

  const doUnsubscribe = async (kind) => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/v1/email/unsubscribe/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, kind, campaign_id: campaignId }),
      });
      if (r.ok) setDone(kind);
    } finally {
      setSubmitting(false);
    }
  };

  const doResubscribe = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/v1/email/resubscribe/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (r.ok) setDone("resubscribed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Wrap><Card><Text>Chargement…</Text></Card></Wrap>;
  }

  if (!token || !info?.valid) {
    return (
      <Wrap>
        <Card>
          <Title>Lien invalide</Title>
          <Text>{info?.error || "Ce lien de désabonnement n'est pas valide ou a expiré."}</Text>
          <Btn onClick={() => window.location.href = "/"}>Retour à l'accueil</Btn>
        </Card>
      </Wrap>
    );
  }

  // Confirmation après action
  if (done) {
    return (
      <Wrap>
        <Card>
          <div style={{fontSize:"2.5rem",marginBottom:".5rem"}}>
            {done === "resubscribed" ? "✅" : "👋"}
          </div>
          <Title>
            {done === "resubscribed" ? "Réabonné !" : "Désabonnement confirmé"}
          </Title>
          <Text>
            {done === "resubscribed"
              ? "Tu recevras à nouveau nos emails."
              : done === "all"
              ? "Tu ne recevras plus aucun email marketing de notre part."
              : "Tu ne recevras plus nos newsletters. Tu continueras à recevoir les emails importants liés à ton compte."}
          </Text>
          {done !== "resubscribed" && (
            <Btn onClick={doResubscribe} disabled={submitting}>
              ↺ Me réabonner
            </Btn>
          )}
          <Btn onClick={() => window.location.href = "/"}>Retour au site</Btn>
        </Card>
      </Wrap>
    );
  }

  // Si déjà désabonné
  if (info.already_unsubscribed) {
    return (
      <Wrap>
        <Card>
          <Title>Déjà désabonné</Title>
          <Text>
            L'adresse <Email>{info.email}</Email> est déjà désabonnée de nos emails marketing.
          </Text>
          <Btn onClick={doResubscribe} disabled={submitting}>↺ Me réabonner</Btn>
        </Card>
      </Wrap>
    );
  }

  // Page principale
  return (
    <Wrap>
      <Card>
        <Title>Se désabonner</Title>
        <Text>
          Tu es sur le point de te désabonner avec l'adresse <Email>{info.email}</Email>.
          <br/><br/>
          Que souhaites-tu faire ?
        </Text>
        <div style={{display:"flex",flexDirection:"column",gap:".5rem"}}>
          <Btn onClick={() => doUnsubscribe("marketing")} disabled={submitting}>
            📭 Ne plus recevoir les newsletters
          </Btn>
          <Btn $danger onClick={() => doUnsubscribe("all")} disabled={submitting}>
            🚫 Ne plus recevoir aucun email marketing
          </Btn>
        </div>
        <Text style={{marginTop:"1.5rem",fontSize:".72rem"}}>
          Tu continueras à recevoir les emails importants liés à ton compte
          (confirmations de dépôt, retraits, etc.) dans tous les cas.
        </Text>
      </Card>
    </Wrap>
  );
}